/**
 * SlotGridEditor — генератор + редактор сетки слотов квеста (эталон
 * screen-17-quests.js gen/gridEd, js:283-356). Правки идут в draft (копия
 * slots) — карточка dirty; пара Сохранить (PUT /api/slots/bulk) / Отмена —
 * только при draft. Шаг генератора — ДОСЛОВНО js:297-304: teaOk =
 * (t+dur)<cutoff ? teaBefore : teaAfter (чайная после игры), за полночь t НЕ
 * заворачивается, сорт по key (якорь 06:00), лимит 64. Конфиг чайных —
 * branchConfig.ts (БД), ночное окно — branches.night_* (A24). Drag:
 * long-press 350мс (движение >8px отменяет), reorder по elementFromPoint,
 * флаг dragged глушит последующий тап-в-правку.
 */
import { useEffect, useRef, useState, type PointerEvent as ReactPointerEvent } from "react";
import { DEFAULT_NIGHT_CUTOFF, DEFAULT_NIGHT_END } from "@ub/shared";
import { TimePicker } from "../../components/shared";
import { getBranchConfig, TEA_MIN_DEFAULT } from "../../hooks/branchConfig";
import type { Branch } from "../../api/types";
import { ActionPair, LpTip } from "./QuestFormCells";
import { BoltIco } from "./questIcons";
import { SlotChip } from "./SlotChip";
import { ft, keyOf, maskT, normT, tm } from "./slotTime";
import cfg from "./config.module.css";
import css from "./quests.module.css";

export interface SlotGridEditorProps {
  dur: number;
  reset: number;
  branch: Branch | null;
  slots: string[];
  draft: string[] | null;
  onDraft: (next: string[] | null) => void;
  onSave: (times: string[]) => void;
}

export function SlotGridEditor({ dur, reset, branch, slots, draft, onDraft, onSave }: SlotGridEditorProps) {
  const [gen, setGen] = useState({ from: "10:00", to: "23:30" });
  const [tp, setTp] = useState(false);
  const [err, setErr] = useState("");
  const [edit, setEdit] = useState<{ i: number; fresh: boolean } | null>(null);
  const [editVal, setEditVal] = useState("");
  const [editBad, setEditBad] = useState(false);
  const [addVal, setAddVal] = useState("");
  const [addBad, setAddBad] = useState(false);
  const [dragOn, setDragOn] = useState(-1);
  const editRef = useRef(edit); editRef.current = edit;
  const editValRef = useRef(editVal); editValRef.current = editVal;
  const dragIdx = useRef(-1), dragPid = useRef(-1);
  const draggedRef = useRef(false);
  const lpT = useRef(0), lpXY = useRef({ x: 0, y: 0 });
  const editInp = useRef<HTMLInputElement>(null);
  const gridRef = useRef<string[]>([]);

  const bc = getBranchConfig(branch ? [branch] : [], branch?.id ?? "");
  const cutoff = bc.teaCutoff ?? "19:00";
  const teaMin = bc.teaMin ?? TEA_MIN_DEFAULT;
  const night0 = branch?.night_cutoff || DEFAULT_NIGHT_CUTOFF;
  const night1 = branch?.night_end || DEFAULT_NIGHT_END;
  const grid = draft ?? slots;
  gridRef.current = grid;

  const mut = (fn: (arr: string[]) => string[]) => onDraft(fn([...gridRef.current]));

  useEffect(() => {
    if (!edit?.fresh) return;
    editInp.current?.focus();
    setEdit((e) => (e ? { ...e, fresh: false } : e));
  }, [edit]);
  useEffect(() => () => window.clearTimeout(lpT.current), []);

  const isNight = (t: string): boolean => {
    const m = tm(t), a = tm(night0), b = tm(night1);
    return m !== null && a !== null && b !== null && (m >= a || m < b);
  };

  /* Генератор — ДОСЛОВНО эталон js:297-304. */
  const generate = () => {
    const fm = tm(gen.from), to = tm(gen.to);
    if (fm === null || to === null) { setErr("Введите старт HH:MM"); return; }
    const end = to <= fm ? to + 1440 : to; /* через полночь */
    const out: string[] = [];
    const cut = tm(cutoff) ?? 0;
    let t = fm;
    while (t <= end && out.length < 64) {
      out.push(ft(t));
      /* чайная — после игры; за полночь t не заворачиваем: «после отсечки» */
      const teaOk = t + dur < cut ? !!bc.teaBefore : !!bc.teaAfter;
      t += dur + reset + (teaOk ? teaMin : 0);
    }
    if (!out.length) { setErr("Нет слотов в этом диапазоне"); return; }
    setErr("");
    out.sort((a, b) => keyOf(a) - keyOf(b));
    onDraft(out);
    setEdit(null);
  };

  let steps = `Шаг: ${dur + reset}м (игра ${dur} + пауза ${reset})`;
  if (bc.teaBefore || bc.teaAfter) {
    const scope = bc.teaBefore && bc.teaAfter ? "" : bc.teaBefore ? ` (до ${cutoff})` : ` (после ${cutoff})`;
    steps += ` · ${dur + teaMin + reset}м с чайной${scope}`;
  }
  steps += ` · ночное окно ${night0}–${night1}`;

  const commitEdit = (i: number, ok: boolean) => {
    if (ok) {
      const v = normT(editValRef.current);
      if (!v) { setEditBad(true); return; }
      mut((arr) => { arr[i] = v; return arr; });
    }
    setEdit(null);
  };
  /* deferred blur: не убивать клик по соседней кнопке (эталон js:339). */
  const blurEdit = (i: number, t: string) => window.setTimeout(() => {
    if (editRef.current?.i !== i) return;
    const v = normT(editValRef.current);
    if (v && v !== t) mut((arr) => { arr[i] = v; return arr; });
    setEdit(null);
  }, 0);
  const startEdit = (i: number, t: string) => {
    if (draggedRef.current) return;
    setEdit({ i, fresh: true }); setEditVal(t); setEditBad(false);
  };

  const commitAdd = () => {
    if (!addVal) { setAddBad(false); return; }
    const v = normT(addVal);
    if (!v) { setAddBad(true); return; }
    mut((arr) => [...arr, v]); setAddVal("");
  };
  const commitAddRef = useRef(commitAdd); commitAddRef.current = commitAdd;

  const dragDown = (i: number, e: ReactPointerEvent<HTMLElement>) => {
    /* Эталон js:348 исключает ТОЛЬКО ✕ (data-sx) — время остаётся drag-
       поверхностью. Однопальцевая модель: второй палец игнорируется (у
       эталона состояние per-chip в замыкании — коллизии нет). */
    if ((e.target as Element).closest("[data-sx]")) return;
    if (dragIdx.current >= 0 || dragPid.current !== -1) return;
    dragPid.current = e.pointerId;
    const el = e.currentTarget, pid = e.pointerId;
    lpXY.current = { x: e.clientX, y: e.clientY };
    lpT.current = window.setTimeout(() => {
      draggedRef.current = true;
      dragIdx.current = i;
      setDragOn(i);
      try { el.setPointerCapture(pid); } catch { /* уже отпущен */ }
    }, 350);
  };
  const dragMove = (e: ReactPointerEvent<HTMLElement>) => {
    if (e.pointerId !== dragPid.current) return;
    if (dragIdx.current < 0) {
      if (Math.hypot(e.clientX - lpXY.current.x, e.clientY - lpXY.current.y) > 8) {
        window.clearTimeout(lpT.current);
      }
      return;
    }
    const tgt = document.elementFromPoint(e.clientX, e.clientY)?.closest("[data-si]");
    if (!(tgt instanceof HTMLElement) || tgt.dataset.si === undefined) return;
    const to = Number(tgt.dataset.si), from = dragIdx.current;
    if (to === from || to < 0 || to >= gridRef.current.length) return;
    mut((arr) => { const [x] = arr.splice(from, 1); arr.splice(to, 0, x!); return arr; });
    dragIdx.current = to;
    setDragOn(to);
  };
  const dragEnd = (e: ReactPointerEvent<HTMLElement>) => {
    if (e.pointerId !== dragPid.current) return;
    dragPid.current = -1; window.clearTimeout(lpT.current);
    if (dragIdx.current < 0) return;
    dragIdx.current = -1; setDragOn(-1);
    window.setTimeout(() => { draggedRef.current = false; }, 0);
  };
  /* Мышь ушла до long-press: снять таймер/pid (touch держит implicit capture — leave не придёт). */
  const dragLeave = (e: ReactPointerEvent<HTMLElement>) => {
    if (e.pointerId !== dragPid.current || dragIdx.current >= 0) return;
    dragPid.current = -1; window.clearTimeout(lpT.current);
  };

  return (
    <>
      {/* sepTop — правило .qbody .apair+.gap2 (html:294): генератор идёт сразу после ActionPair формы */}
      <div className={`${css.gap2} ${css.sepTop}`}>
        <div className={cfg.secRow}>
          <span style={{ color: "var(--cyan)", lineHeight: 0 }}><BoltIco /></span>
          <h4 className={`${cfg.secT} ${cfg.cy}`} style={{ flex: "0 1 auto" }}>
            <LpTip text={steps}>Генератор слотов</LpTip>
          </h4>
        </div>
        <div className={css.genrow}>
          <button type="button" className={cfg.teNumbtn} onClick={() => setTp(true)}>
            <span className={css.num}>{gen.from}–{gen.to}</span>
          </button>
          <button type="button" className={`${css.abtn} ${css.solo} ${css.genBtn}`} onClick={generate}>
            <BoltIco />Сгенерировать
          </button>
          {err ? <span className={css.err9}>{err}</span> : <span style={{ color: "var(--text-3)", fontSize: 10 }}>{steps}</span>}
        </div>
      </div>
      <div className={css.gap2}>
        <div className={cfg.secRow}>
          <h4 className={cfg.secT} style={{ flex: "0 1 auto" }}>
            <LpTip text="Тап — править · Зажать — перетащить · ✕ — удалить · Сохранить — в БД">
              Сетка слотов (<span className={css.num}>{grid.length}</span>)
            </LpTip>
          </h4>
        </div>
        <div className={css.slots}>
          {grid.map((t, i) => (
            <SlotChip
              key={i} t={t} i={i} night={isNight(t)} dragOn={dragOn === i}
              editing={edit?.i === i} editVal={editVal} editBad={editBad} editInp={editInp}
              onEditChange={(v) => { setEditBad(false); setEditVal(maskT(v)); }}
              onEditCommit={commitEdit} onEditBlur={blurEdit} onEditStart={startEdit}
              onDelete={(x) => { setEdit(null); mut((arr) => { arr.splice(x, 1); return arr; }); }}
              onDown={dragDown} onMove={dragMove} onUp={dragEnd} onLeave={dragLeave}
            />
          ))}
          <input
            className={`${css.sinp} ${addBad ? css.bad : ""}`}
            placeholder="HH:MM"
            inputMode="numeric"
            value={addVal}
            onChange={(e) => { setAddBad(false); setAddVal(maskT(e.target.value)); }}
            onKeyDown={(e) => {
              if (e.key === "Enter") commitAdd();
              else if (e.key === "Escape") { setAddVal(""); setAddBad(false); }
            }}
            onBlur={() => window.setTimeout(() => commitAddRef.current(), 0)}
          />
        </div>
        {draft ? (
          <ActionPair
            okLabel="Сохранить"
            onOk={() => { onSave(draft); onDraft(null); setEdit(null); }}
            onCancel={() => { onDraft(null); setEdit(null); }}
          />
        ) : null}
      </div>
      <TimePicker
        open={tp}
        label="Диапазон генерации"
        mode="range"
        step={5}
        value={`${gen.from}–${gen.to}`}
        onCommit={(v) => {
          const [a, b] = v.split(/[–-]/);
          if (a && b) setGen({ from: a, to: b });
          setTp(false);
        }}
        onCancel={() => setTp(false)}
      />
    </>
  );
}
